import { useEffect } from "react";
import type { ReactNode } from "react";
import { tokens } from "@core/theme/tokens";
import { LanguageSwitch, type ContentLanguage } from "./LanguageSwitch";
import { DialogHeader } from "./DialogHeader";
import { AlertText } from "./NoticeText";
import { Modal } from "./PortalPrimitives";

// SPEC-201 §4.2: obligatorios marcados antes de enviar, validación de
// servidor agrupada junto al campo, confirmación al salir con cambios sin
// guardar, conmutador de idioma dentro del formulario para contenido
// bilingüe.

export function RequiredMark() {
  return (
    <span aria-hidden style={{ color: tokens.danger.base, marginLeft: 3 }}>
      *
    </span>
  );
}

// El estilo vive ahora en `AlertText` (NoticeText.tsx, punto #4C de la revisión
// en vivo 2026-09-22): mismo `role`, misma tipografía, mismo color y el mismo
// `marginTop` — el DOM que renderiza este componente no cambió, solo dejó de
// ser la única copia de ese estilo.
export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <AlertText style={{ marginTop: 4 }}>{message}</AlertText>;
}

/**
 * Bloquea la salida (navegación del navegador) cuando `dirty` es true, sin
 * reimplementar reglas de negocio: solo pregunta antes de descartar cambios.
 */
export function useConfirmUnsavedChanges(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [dirty]);
}

export function FormLayout({
  title,
  eyebrow,
  language,
  onLanguageChange,
  bilingual = false,
  onSubmit,
  children,
  actions,
  modal,
}: {
  // SPEC-202, finding de diseño de la ola 2: el formulario de "Datos
  // generales" ahora vive siempre visible dentro de su pestaña, sin un
  // encabezado propio (el título de la pestaña ya lo cumple; el prototipo,
  // admin.jsx:895-925, tampoco repite un título dentro del contenido de la
  // pestaña) — `title` pasa a opcional para que ese caller pueda omitirlo
  // sin afectar a quienes sí lo usan (p. ej. el modal de "Nuevo miembro").
  title?: string | undefined;
  // Punto #21: texto chico sobre el título cuando el formulario vive en un
  // `Modal` (sin él se usa el `label` del diálogo). Inline no se muestra.
  eyebrow?: string | undefined;
  language?: ContentLanguage;
  onLanguageChange?: (lang: ContentLanguage) => void;
  bilingual?: boolean;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  actions: ReactNode;
  // Revisión en vivo 2026-09-22, puntos #4A/#15: con `modal`, este formulario
  // ES el diálogo — título y acciones dejan de scrollear con el contenido y
  // pasan a las regiones fijas del `Modal`. Sin `modal` no cambia nada: la
  // forma inline (pestaña "Datos generales" de Miembros) sigue idéntica.
  modal?: {
    label: string;
    width?: number | string;
    onClose?: (() => void) | undefined;
  };
}) {
  const languageSwitch =
    bilingual && language && onLanguageChange ? (
      <LanguageSwitch value={language} onChange={onLanguageChange} />
    ) : null;

  // Revisión en vivo 2026-09-22, punto #21: dentro de un `Modal` el encabezado
  // es el mismo de todos los diálogos (`DialogHeader`: eyebrow + título
  // `h3.display`); el selector de idioma, si lo había, sigue a la derecha. La
  // forma inline (pestaña "Datos generales" de Miembros) conserva su título
  // chico de 15px.
  const heading = modal ? (
    title ? (
      <DialogHeader
        eyebrow={eyebrow ?? modal.label}
        title={title}
        aside={languageSwitch}
      />
    ) : languageSwitch ? (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {languageSwitch}
      </div>
    ) : null
  ) : title || languageSwitch ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 20,
      }}
    >
      {title ? (
        <h3
          style={{
            font: `600 15px ${tokens.font.display}`,
            color: tokens.text.primary,
            margin: 0,
          }}
        >
          {title}
        </h3>
      ) : (
        <div />
      )}
      {languageSwitch}
    </div>
  ) : null;

  const body = (
    <>
      {bilingual && language === "en" ? (
        <div
          role="status"
          style={{
            margin: "0 0 16px",
            padding: "10px 12px",
            borderRadius: 11,
            border: `1px solid ${tokens.surface.line}`,
            background: tokens.surface.surface,
            color: tokens.text.secondary,
            font: `400 12.5px ${tokens.font.ui}`,
          }}
        >
          Los campos sin traducir se mostrarán en español.
        </div>
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {children}
      </div>
    </>
  );

  if (modal) {
    // El `<form>` envuelve al `Modal` entero, no al cuerpo: así los botones
    // del pie (que son `type="submit"`) siguen siendo descendientes del
    // formulario y `onSubmit` se dispara igual que antes, sin `form=<id>` ni
    // `requestSubmit`. `display: contents` deja al `<form>` sin caja propia —
    // el diálogo se sigue posicionando contra el viewport, no contra él.
    return (
      <form onSubmit={onSubmit} style={{ display: "contents" }}>
        <Modal
          label={modal.label}
          {...(modal.width !== undefined ? { width: modal.width } : {})}
          {...(modal.onClose ? { onClose: modal.onClose } : {})}
          {...(heading ? { header: heading } : {})}
          footer={
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              {actions}
            </div>
          }
        >
          {body}
        </Modal>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      {heading}
      {body}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 24,
        }}
      >
        {actions}
      </div>
    </form>
  );
}
